import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { FeatureSettingsService } from '../shared/feature-settings.service';

import { firstValueFrom } from 'rxjs';

export interface TrackSector {
	sector: number;
	color: string;
	points: number[]; // flat [x0, y0, x1, y1, ...]
}

export interface TrackTurn {
	label: string;
	x: number;
	y: number;
}

export interface TrackData {
	points: number[];
	sectors: TrackSector[];
	pitLane: number[];
	startfinish: number[];
	turns: TrackTurn[];
}

@Injectable({
	providedIn: 'root',
})
export class TrackRendererService {
	private http = inject(HttpClient);
	private featureService = inject(FeatureSettingsService);

	public trackName = signal('');

	private trackData: TrackData = {
		points: [],
		sectors: [],
		startfinish: [],
		pitLane: [],
		turns: [],
	};

	private trackOrigin: [number, number] = [0, 0];

	constructor() {
		this.featureService.featureSettings$.subscribe((settings) => {
			if (settings.track) {
				this.trackName.set(settings.track.fileName);
			}
		});
	}

	async loadTrack(): Promise<void> {
		if (!this.trackName()) {
			return;
		}
		const path = `/assets/tracks/${this.trackName()}`;
		const jsonText = await firstValueFrom(this.http.get(path, { responseType: 'text' }));
		const geojson = JSON.parse(jsonText);

		this.trackData = { points: [], sectors: [], pitLane: [], startfinish: [], turns: [] };

		if (geojson.type === 'FeatureCollection') {
			this.loadFeatureCollection(geojson);
		} else {
			console.error(`GeoJson expected to by type: FeatureCollection but found: ${geojson.type}`);
		}
	}

	private loadFeatureCollection(geojson: any): void {
		const features: any[] = geojson.features;

		const firstLine = features.find(
			(f) =>
				f.geometry?.type === 'LineString' &&
				(f.properties?.type === 'sector' || f.properties?.type === 'pitlane'),
		);
		if (!firstLine) return;
		this.setOrigin(firstLine.geometry.coordinates);

		const sectors: TrackSector[] = [];

		for (const feature of features) {
			const props = feature.properties ?? {};
			const geomType = feature.geometry?.type;
			const featureType = props.type;

			if (featureType === 'sector' && geomType === 'LineString') {
				const points = this.coordsToFlat(feature.geometry.coordinates);
				sectors.push({
					sector: props.sector ?? sectors.length + 1,
					color: props.color ?? '#ffffff',
					points,
				});
			} else if (featureType === 'pitlane' && geomType === 'LineString') {
				this.trackData.pitLane = this.coordsToFlat(feature.geometry.coordinates);
			} else if (featureType === 'startfinish' && geomType === 'LineString') {
				this.trackData.startfinish = this.coordsToFlat(feature.geometry.coordinates);
			} else if (featureType === 'turn' && geomType === 'Point') {
				const [lon, lat] = feature.geometry.coordinates;
				const [x, y] = this.lonLatToXY(lon, lat);
				this.trackData.turns.push({ label: props.label ?? '', x, y });
			}
		}

		sectors.sort((a, b) => a.sector - b.sector);
		this.trackData.sectors = sectors;
		this.trackData.points = sectors.flatMap((s) => s.points);
	}

	private setOrigin(coords: [number, number][]): void {
		this.trackOrigin = [coords[0][0], coords[0][1]];
	}

	private coordsToFlat(coords: [number, number][]): number[] {
		return coords.flatMap(([lon, lat]) => {
			const [x, y] = this.lonLatToXY(lon, lat);
			return [x, y];
		});
	}

	private lonLatToXY(lon: number, lat: number): [number, number] {
		const [originLon, originLat] = this.trackOrigin;
		const x = (lon - originLon) * 111_230.0 * Math.cos((lat * Math.PI) / 180);
		const y = -((lat - originLat) * 110_540.0); // negated: canvas Y increases downward
		return [x, y];
	}

	getPoints(): number[] {
		return this.trackData.points;
	}

	getSectors(): TrackSector[] {
		return this.trackData.sectors;
	}

	getPitLane(): number[] {
		return this.trackData.pitLane;
	}

	getStartFinish(): number[] {
		return this.trackData.startfinish;
	}

	getTurns(): TrackTurn[] {
		return this.trackData.turns;
	}

	latLonToXY(lat: number, lon: number): [number, number] {
		if (this.trackData.points.length === 0) throw new Error('Track not yet loaded');
		return this.lonLatToXY(lon, lat);
	}

	isLoaded(): boolean {
		return this.trackData.points.length > 0;
	}
}
